import {
    Globe,
    Smartphone,
    Database,
    CircuitBoard,
    Shield,
    Terminal,
    ArrowRight,
    Star,
    Clock,
    CheckCircle2,
    Sparkles,
    Zap
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const services = [
    {
        title: 'Desenvolvimento Web',
        description: 'Sites institucionais, e-commerces e aplicações web modernas com foco em performance e conversão.',
        icon: Globe,
        features: ['Sites Responsivos', 'E-commerce', 'SEO Otimizado', 'CMS Personalizado'],
        price: 'A partir de R$ 2.500',
        deliveryTime: '15-30 dias',
        popular: false,
        gradient: 'from-blue-500 to-cyan-500'
    },
    {
        title: 'Aplicativos Mobile',
        description: 'Apps nativos e híbridos para iOS e Android com performance excepcional e UX moderna.',
        icon: Smartphone,
        features: ['iOS & Android', 'Push Notifications', 'Offline First', 'App Store Deploy'],
        price: 'A partir de R$ 8.000',
        deliveryTime: '45-90 dias',
        popular: true,
        gradient: 'from-purple-500 to-pink-500'
    },
    {
        title: 'Sistemas ERP/CRM',
        description: 'Gestão completa de negócios com integrações personalizadas e relatórios avançados.',
        icon: Database,
        features: ['Gestão Completa', 'Relatórios BI', 'Integrações API', 'Multi-usuário'],
        price: 'A partir de R$ 15.000',
        deliveryTime: '60-120 dias',
        popular: false,
        gradient: 'from-green-500 to-emerald-500'
    },
    {
        title: 'Jogos Digitais',
        description: 'Desenvolvimento de jogos 2D e 3D para múltiplas plataformas e dispositivos.',
        icon: CircuitBoard,
        features: ['2D & 3D', 'Multi-plataforma', 'Monetização', 'Analytics'],
        price: 'Sob consulta',
        deliveryTime: '90-180 dias',
        popular: false,
        gradient: 'from-orange-500 to-red-500'
    },
    {
        title: 'Cibersegurança',
        description: 'Pentests, auditoria de segurança e implementação de proteções avançadas.',
        icon: Shield,
        features: ['Pentest Completo', 'Auditoria', 'Correções', 'Monitoramento'],
        price: 'A partir de R$ 3.500',
        deliveryTime: '7-15 dias',
        popular: false,
        gradient: 'from-indigo-500 to-purple-500'
    },
    {
        title: 'Automação',
        description: 'Bots e automações inteligentes para otimizar processos e reduzir custos operacionais.',
        icon: Terminal,
        features: ['Bots Personalizados', 'API Integration', 'Workflows', 'Relatórios'],
        price: 'A partir de R$ 1.800',
        deliveryTime: '10-25 dias',
        popular: false,
        gradient: 'from-teal-500 to-blue-500'
    },
];

const ServicesSection = () => {
    const backgroundImages = [
        'https://ik.imagekit.io/AlphaLabs/webapp.webp?tr=w-800,q-75,fo-auto,f-webp',
        'https://ik.imagekit.io/AlphaLabs/aplicativos-mobile.webp?updatedAt=1756931649119',
        'https://ik.imagekit.io/AlphaLabs/erp.webp?tr=w-600,q-75,fo-auto,f-webp',
        'https://ik.imagekit.io/AlphaLabs/games.webp?tr=w-600,q-75,fo-auto,f-webp',
        'https://ik.imagekit.io/AlphaLabs/cyber.webp?tr=w-600,q-75,fo-auto,f-webp',
        'https://ik.imagekit.io/AlphaLabs/scripts.webp?tr=w-600,q-75,fo-auto,f-webp'
    ];

    return (
        <section className="relative py-8 overflow-hidden bg-gradient-to-br from-primary/10 via-accent/5 to-primary/15">
            {/* Background Effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full filter blur-3xl opacity-40 animate-pulse"></div>
                <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/10 rounded-full filter blur-3xl opacity-40 animate-pulse delay-1000"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary/5 to-accent/5 rounded-full blur-3xl opacity-30"></div>
            </div>

            <div className="container-page relative z-10">
                {/* Header */}
                <div className="text-center mb-16 space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary">
                        <Sparkles className="w-4 h-4" />
                        <span>Soluções Completas para seu Negócio</span>
                    </div>

                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                        Nossos{' '}
                        <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                            Serviços
                        </span>
                    </h2>

                    <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                        Soluções completas para <strong className="text-primary">impulsionar seu negócio digital</strong> com
                        tecnologia de ponta e metodologia comprovada.
                    </p>
                </div>

                {/* Services Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                    {services.map((service, index) => (
                        <div key={service.title} className="group relative">
                            {/* Popular Badge */}
                            {service.popular && (
                                <div className="absolute -top-3 -right-3 z-30 bg-gradient-to-r from-primary to-accent text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                    <Star className="w-3 h-3 fill-current" />
                                    Mais Popular
                                </div>
                            )}

                            <div className={`relative h-full rounded-2xl border overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-2xl ${service.popular ? 'border-primary/50 shadow-lg' : 'border-border/50'
                                }`}>

                                {/* Background Image */}
                                <div
                                    className="absolute inset-0 opacity-40 bg-cover bg-center transition-opacity duration-300 group-hover:opacity-50"
                                    style={{ backgroundImage: `url('${backgroundImages[index]}')` }}
                                />

                                {/* Gradient Overlay */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${service.popular ? 'from-primary/10 via-background600 to-accent/10' : 'from-background/40 via-background/40 to-background/40'} transition-all duration-300`} />

                                <div className="relative z-10 p-6 h-full flex flex-col">
                                    {/* Icon & Title */}
                                    <div className="mb-6">
                                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:shadow-xl transition-all duration-300`}>
                                            <service.icon className="w-8 h-8 text-white" />
                                        </div>

                                        <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                                            {service.title}
                                        </h3>

                                        <p className="text-muted-foreground leading-relaxed">
                                            {service.description}
                                        </p>
                                    </div>

                                    {/* Features */}
                                    <div className="mb-6 flex-1">
                                        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-primary" />
                                            Inclui:
                                        </h4>
                                        <ul className="space-y-2">
                                            {service.features.map((feature, idx) => (
                                                <li key={idx} className="flex items-center text-sm text-muted-foreground">
                                                    <div className="w-1.5 h-1.5 bg-primary rounded-full mr-3 flex-shrink-0" />
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Pricing & Timeline */}
                                    <div className="space-y-4">
                                        <div className={`p-4 rounded-xl border ${service.popular ? 'bg-primary/10 border-primary/30' : 'bg-muted/50 border-border/50'}`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="text-lg font-bold text-primary">
                                                    {service.price}
                                                </div>
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Clock className="w-3 h-3" />
                                                    {service.deliveryTime}
                                                </div>
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                Suporte incluso por 6 meses
                                            </div>
                                        </div>

                                        {/* CTA Button */}
                                        <Button
                                            asChild
                                            className={`w-full ${service.popular ?
                                                'bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90' :
                                                'bg-primary hover:bg-primary/90'
                                                } hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl`}
                                        >
                                            <Link to="/contato">
                                                Solicitar Orçamento
                                                <ArrowRight className="ml-2 w-4 h-4" />
                                            </Link>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom CTA Section */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary/10 via-background to-accent/10 border p-4 md:p-8 ">
                    {/* Background Image */}
                    <div
                        className="absolute inset-0 opacity-20 bg-center bg-cover "
                        style={{
                            backgroundImage: `url('')`,
                            
                        }}
                    />

                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-50" />

                    <div className="relative z-10 text-center space-y-4">
                        <div className="space-y-4">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-full text-sm font-medium text-primary">
                                <Zap className="w-4 h-4" />
                                <span>Consultoria Especializada</span>
                            </div>

                            <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                                Não encontrou
                                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"> o que precisa?</span>
                            </h3>

                            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                                Desenvolvemos <strong className="text-primary">soluções personalizadas</strong> para qualquer necessidade do seu negócio.
                                Nossa equipe está pronta para transformar sua ideia em realidade.
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <Button
                                size="lg"
                                className="text-lg px-8 py-6 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                                asChild
                            >
                                <Link to="/contato">
                                    Falar com Especialista
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Link>
                            </Button>

                            <Button
                                variant="outline"
                                size="lg"
                                className="text-lg px-8 py-6 border-primary/30 hover:bg-accent hover:text-accent-foreground hover:scale-105 transition-all duration-300"
                                asChild
                            >
                                <Link to="/portfolio">
                                    Ver Projetos
                                    <CircuitBoard className="ml-2 w-5 h-5" />
                                </Link>
                            </Button>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-6 border-t border-border/50">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <CheckCircle2 className="w-4 h-4 text-primary" />
                                <span>Consultoria gratuita</span>
                            </div>
                            <div className="hidden sm:block w-px h-4 bg-border" />
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Star className="w-4 h-4 text-primary" />
                                <span>Orçamento em 24h</span>
                            </div>
                            <div className="hidden sm:block w-px h-4 bg-border" />
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Shield className="w-4 h-4 text-primary" />
                                <span>Garantia de qualidade</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ServicesSection;